/**
 * File export / import for the browser.
 *
 * Uses the Web Share API when the platform can share files — phones, and
 * desktop Chrome too — and falls back to an anchor download. Both branches
 * live here so that the Capacitor implementation is a sibling file, not a
 * refactor.
 */

import type { FileExporter } from './types.ts';

/** The slice of the browser `saveBlob` touches, so the choice of branch can be tested without one. */
export interface ExportPlatform {
  readonly navigator: {
    readonly canShare?: (data: ShareData) => boolean;
    readonly share?: (data: ShareData) => Promise<void>;
  };
  readonly document: {
    createElement(tag: 'a'): { href: string; download: string; rel: string; click(): void; remove(): void };
    readonly body: { appendChild(node: unknown): unknown };
  };
  readonly url: {
    createObjectURL(blob: Blob): string;
    revokeObjectURL(url: string): void;
  };
}

/** The real browser, looked up at call time so the share API is seen as it is when the coach clicks. */
function browserPlatform(): ExportPlatform {
  return {
    navigator: {
      canShare: (data) => typeof navigator.canShare === 'function' && navigator.canShare(data),
      share: (data) => navigator.share(data),
    },
    document,
    url: URL,
  };
}

export class WebFileExporter implements FileExporter {
  readonly #platform: ExportPlatform;

  constructor(platform?: ExportPlatform) {
    this.#platform = platform ?? browserPlatform();
  }

  async saveBlob(blob: Blob, filename: string): Promise<void> {
    const { navigator, document, url } = this.#platform;
    const file = new File([blob], filename, { type: blob.type });

    if (navigator.canShare && navigator.share && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({ files: [file], title: filename });
        return;
      } catch (error) {
        // The coach dismissed the sheet: done. Anything else — typically
        // NotAllowedError, the user activation having lapsed — falls through
        // to a download, so the file is never lost.
        if ((error as DOMException).name === 'AbortError') return;
      }
    }

    const href = url.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = href;
    anchor.download = filename;
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    // Give Safari a beat before revoking.
    setTimeout(() => url.revokeObjectURL(href), 4000);
  }

  openText(accept: string): Promise<{ name: string; text: string } | undefined> {
    return new Promise((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = accept;
      input.onchange = async () => {
        const file = input.files?.[0];
        if (!file) {
          resolve(undefined);
          return;
        }
        resolve({ name: file.name, text: await file.text() });
      };
      input.oncancel = () => resolve(undefined);
      input.click();
    });
  }
}

/** Canvas -> PNG blob. Kept next to the exporter so the UI has one import site. */
export function canvasToPngBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('Could not encode PNG'));
    }, 'image/png');
  });
}
